module.exports={render:function (){var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;
  return _vm._m(0)
},staticRenderFns: [function (){var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;
  return _c('div', {
    staticClass: "chapter"
  }, [_c('div', {
    staticClass: "inner_page"
  }, [_c('h1', {
    staticClass: "chapter-no",
    attrs: {
      "id": "ch-2"
    }
  }, [_c('a', {
    attrs: {
      "id": "page_24"
    }
  }, [_vm._v("2. The “End” of the Cosmos and Life in Natural Sciences’ Conjectures")])]), _c('a', {
    attrs: {
      "id": "page_24"
    }
  }, [_c('h1', {
    staticClass: "sec1",
    attrs: {
      "id": "sec-4"
    }
  }, [_c('strong', [_vm._v("The Origins of the Universe in Light of Contemporary Science")])])]), _c('p', {
    staticClass: "para1"
  }, [_c('a', {
    attrs: {
      "id": "page_24"
    }
  }, [_vm._v("The sciences’ conjectures about the future are based on our current best knowledge about the cosmos’s origins, evolvement, and workings. While a detailed discussion of various aspects of scientific cosmology’s views of origins and their relation to theological reflection is conducted in "), _c('em', [_vm._v("Creation and Humanity")]), _vm._v(" (part 1), we will summarize very briefly the key ideas as background to the current investigation."), _c('sup')]), _c('a', {
    attrs: {
      "href": "#/#ft1",
      "id": "fn1"
    }
  }, [_vm._v("1")]), _vm._v(" Behind the contemporary cosmological theories about origins and the “end” lie radical shifts in worldview and outlook.")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("Rather than steady, mechanistic, and dualistic, the workings of the cosmos bespeak relationality, interdependence, dynamism, evolvement, and complexity. These are the results of the move from the Newtonian worldview to that driven by the contemporary relativity and quantum theory. What quantum theory reveals is that not only at the smallest, subatomic level (where it primarily functions), but also at the macrolevel, nature reveals surprises, irregularities, and unpredictability. This is not to say that nature acts unlawfully; the laws of nature are still in place and natural phenomena are (relatively speaking) deterministic; otherwise no scientific observations would be possible. What the unpredictability means is that determinism is not ironclad "), _c('a', {
    attrs: {
      "id": "page_25"
    }
  }, [_vm._v("and that—according to the major (Copenhagian) interpretation of quantum theory—natural processes and events are probabilistic in nature. The lack of exact results is not a matter of weakness of measurement but an inherent feature of the reality studied."), _c('sup')]), _c('a', {
    attrs: {
      "href": "#/#ft2",
      "id": "fn2"
    }
  }, [_vm._v("2")])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("This kind of world is open rather than closed in nature. In the open universe, the process of emergence is constantly at work. The basic definition of “emergence” is “that new and unpredictable phenomena are naturally produced by interactions in nature; that these new structures, organisms, and ideas are not reducible to the subsystems on which they depend; and that the newly evolved realities in turn exercise a causal influence on the parts out of which they arose.”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft3",
      "id": "fn3"
    }
  }, [_vm._v("3")])]), _vm._v(" A theologically and eschatologically significant observation is that “emergence at all levels of being, and not just at those of life and mind, requires that nature possess an anticipatory rather than simply a cumulative character. It must be open to a domain of potentiality that makes a quiet entrance—from the future as it were—and thus opens up the otherwise unbending fabric of things to the later-and-more.”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft4",
      "id": "fn4"
    }
  }, [_vm._v("4")])]), _vm._v(" In other words, there is a dynamic tension between increasing entropy and the higher structuring. “In its dependence all creaturely reality is subject to the fate of destructuring, of dissolution according to the law of entropy. Because of the openness of process structures to future events, however, new structures are constantly formed, since processes take place in open rather than closed systems.”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft5",
      "id": "fn5"
    }
  }, [_vm._v("5")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("On the basis of these and related insights and discoveries, contemporary cosmology envisions the origins of the cosmos in terms of big bang cosmologies.")]), _vm._v(" "), _c('h1', {
    staticClass: "sec1",
    attrs: {
      "id": "sec-5"
    }
  }, [_c('strong', [_vm._v("Big Bang Cosmologies")])]), _vm._v(" "), _c('p', {
    staticClass: "para1"
  }, [_vm._v("In the standard big bang theory, currently the basis of all scientific cosmological speculations even among those who advocate revised versions, the cosmos came into being about 13.7 billion years ago from a singularity of zero size and infinite density (usually represented as "), _c('em', [_vm._v("t")]), _vm._v(" = 0, in which "), _c('em', [_vm._v("t")]), _vm._v(" denotes time) and has since expanded to its current form. Supported by relativity theory and quantum physics, it has received remarkable experimental confirmation of its "), _c('a', {
    attrs: {
      "id": "page_26"
    }
  }, [_vm._v("basic intuitions—such as the Hubble discovery in the 1920s that galaxies are receding from us, implying that the cosmos is “expanding,” and the microwave background radiation discovery in the 1960s, which is believed to be an echo from the original big bang.")])]), _c('a', {
    attrs: {
      "id": "page_26"
    }
  }, [_c('p', {
    staticClass: "indent-para"
  }, [_vm._v("The big bang theory simply looks back in time to the point when expansion began and to the “beginning point.” Because of extremely complicated questions, particularly regarding the “inflation period” immediately following the big bang (the so-called Planck time, 1"), _c('sup', [_vm._v("-43")]), _vm._v(" second, the shortest measure of time), to which no known scientific laws apply, revisions of the standard model continue. Most well known is the Hartle/Hawkins quantum model, in which there is no “beginning” in time; the cosmos is finite with regard to its origins. Furthermore, more radical forms of quantum cosmologies are emerging all the time—including various types of “bouncing” or “oscillating” models in which big bounces, rather than one single big bang, succeed each other endlessly, as well as “multiverse” proposals, that is, ours is only one among many, perhaps infinitely many, universes. Theologically speaking, however, none of these poses a serious problem because the most foundational theistic belief (including that of other Abrahamic faiths) has to do with the contingency of the cosmos on the Creator. The world is neither self-originating nor self-sustaining. How the “logistics” might be best understood—the domain of sciences—is neither a threat nor an alternative to creation theology, but rather a necessary dialogue partner.")]), _vm._v(" "), _c('h1', {
    staticClass: "sec1",
    attrs: {
      "id": "sec-6"
    }
  }, [_c('strong', [_vm._v("Scientific Predictions about the “Future” of the Universe")])]), _vm._v(" "), _c('h1', {
    staticClass: "sec2-t0",
    attrs: {
      "id": "sec2-8"
    }
  }, [_c('em', [_vm._v("Dominant Options according to Current Knowledge")])])]), _c('p', {
    staticClass: "para1"
  }, [_c('a', {
    attrs: {
      "id": "page_26"
    }
  }, [_vm._v("It is quite interesting that not only theologians—and more widely, scholars of religions"), _c('sup')]), _c('a', {
    attrs: {
      "href": "#/#ft6",
      "id": "fn6"
    }
  }, [_vm._v("6")]), _vm._v("—until recently have for decades missed an engagement of scientific predictions of the future of the cosmos and life. Counterintuitively, even among the scientific community, the questions of the “end” have not been in the forefront at all. Notwithstanding some emerging work in the science-religion engagement of eschatology and the future of the universe, only baby steps have been taken when compared to the wide and deep interest in the question of beginnings. After massive efforts to understand clearly the origins of the universe, it took until the 1970s–1980s for the focus to include also the "), _c('a', {
    attrs: {
      "id": "page_27"
    }
  }, [_vm._v("far-future fate."), _c('sup')]), _c('a', {
    attrs: {
      "href": "#/#ft7",
      "id": "fn7"
    }
  }, [_vm._v("7")]), _vm._v(" Lately, “physical eschatology” has drawn a lot of interest in cosmology and physics."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft8",
      "id": "fn8"
    }
  }, [_vm._v("8")])]), _vm._v(" Unpredicted advances in science have made the task possible and urgent."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft9",
      "id": "fn9"
    }
  }, [_vm._v("9")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("In principle, the picture painted of the “future” in sciences is not complicated: it leads to eventual decay and annihilation."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft10",
      "id": "fn10"
    }
  }, [_vm._v("10")])]), _vm._v(" That said, the question of “[t]he end of the Universe in scientific terms is both simple and very difficult to predict. The simplicity is that Einstein’s theory of general relativity gives equations that tell us how any universe containing matter and radiation will change with time under the influence of gravity.” That said, in light of current knowledge, there is no final certainty as to whether Einstein’s theory is valid also at the largest cosmic scales."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft11",
      "id": "fn11"
    }
  }, [_vm._v("11")])]), _vm._v(" Interestingly, John D. Barrow, himself a leading physicist who has also done groundbreaking work in physical eschatology, has recently spoken of “the speculative futures market,”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft12",
      "id": "fn12"
    }
  }, [_vm._v("12")])]), _vm._v(" not to dismiss the importance of continuing speculations into the future, but rather to issue a warning to advance carefully and thoughtfully. Among other things, he reminds his fellow scientists that even “constants of nature,” which by definition are supposed to be invariant, might not be so."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft13",
      "id": "fn13"
    }
  }, [_vm._v("13")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("As is well known, three basic options are available on the basis of Einstein’s theory: if an open universe, then the process of expanding eventually leads to “freeze”; if “closed,” the expansion will reach the culmination point and eventually “contract” until it results in “fry,” the big crunch; and if a “flat” curvature, as in the first scenario, it will expand (forever) and finally reach “freeze.”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft14",
      "id": "fn14"
    }
  }, [_vm._v("14")])]), _vm._v(" Most all scientists in recent years have become convinced that the open universe is the correct guess and that its expansion is accelerating ever and ever more rapidly."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft15",
      "id": "fn15"
    }
  }, [_vm._v("15")])]), _vm._v(" "), _c('a', {
    attrs: {
      "id": "page_28"
    }
  }, [_vm._v("If so, what are the implications for the future of the universe—and for life on this earth or life elsewhere?"), _c('sup')]), _c('a', {
    attrs: {
      "href": "#/#ft16",
      "id": "fn16"
    }
  }, [_vm._v("16")]), _vm._v(" It is convenient to divide that question into three interrelated time frames: the near future (the end of life conditions on earth), the distant future (the end of earth, sun, and our solar system), and the far-distant future (the “end” of the whole cosmos)."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft17",
      "id": "fn17"
    }
  }, [_vm._v("17")])]), _vm._v(" Let us work our way from the far-distant end to questions related to the future of humanity on this earth.")]), _vm._v(" "), _c('h1', {
    staticClass: "sec2",
    attrs: {
      "id": "sec2-9"
    }
  }, [_c('em', [_vm._v("The Far-Distant, Distant, and Near-Future Predictions concerning the Cosmos and Life Conditions")])]), _vm._v(" "), _c('p', {
    staticClass: "para1"
  }, [_vm._v("The most distant future of the cosmos, according to most current scientific predictions,"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft18",
      "id": "fn18"
    }
  }, [_vm._v("18")])]), _vm._v(" looks something like the following:")]), _vm._v(" "), _c('ul', [_c('li', [_vm._v("In 5 billion years, the sun will become a red giant, engulfing the orbits of Earth and Mars.")]), _vm._v(" "), _c('li', [_vm._v("In 40–50 billion years, star formation in our galaxy will have ended.")]), _vm._v(" "), _c('li', [_vm._v("In 10"), _c('sup', [_vm._v("12")]), _vm._v(" years, all massive stars will have become neutron stars or black holes."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft19",
      "id": "fn19"
    }
  }, [_vm._v("19")])])]), _vm._v(" "), _c('li', [_vm._v("If the universe is closed, then in 10"), _c('sup', [_vm._v("12")]), _vm._v(" years, the universe will have reached its maximum size and then will recollapse back to a singularity like the original hot big bang.")]), _vm._v(" "), _c('li', [_vm._v("In 10"), _c('sup', [_vm._v("31")]), _vm._v(" years, protons and neutrons will decay into positrons, electrons, neutrinos, and photons.")]), _vm._v(" "), _c('li', [_vm._v("In 10"), _c('sup', [_vm._v("34")]), _vm._v(" years, dead planets, black dwarfs, and neutron stars will disappear, their mass completely converted into energy, leaving only black holes, electron-positron plasma, and radiation. All carbon-based life-forms will inevitably become extinct."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft20",
      "id": "fn20"
    }
  }, [_vm._v("20")])])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para1"
  }, [_c('a', {
    attrs: {
      "id": "page_29"
    }
  }, [_vm._v("The end result of all that is that “proton decay spells ultimate doom for life based on protons and neutrons, like "), _c('em', [_vm._v("Homo sapiens")]), _vm._v(" and all forms of life constructed of atoms.”"), _c('sup')]), _c('a', {
    attrs: {
      "href": "#/#ft21",
      "id": "fn21"
    }
  }, [_vm._v("21")]), _vm._v(" In that light, it does not really matter which of the three above-mentioned scientific scenarios is true (open, closed, or flat); they all lead to ultimate decay. “If the predictions of scientific cosmology do indeed come to pass in the future, ours will be a barren universe devoid of any trace that life had ever existed.”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft22",
      "id": "fn22"
    }
  }, [_vm._v("22")])]), _vm._v(" For all forms of life, the far-distant future is bleak. It is the kind of universe we know that makes life possible—and yet, that universe is “big and old, dark and cold.”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft23",
      "id": "fn23"
    }
  }, [_vm._v("23")])]), _vm._v(" Life on Earth (or, if it cannot be found, on other planets) seems to be an anomaly, unique, and very much a latecomer to the scene—at least in light of our current knowledge.")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("But what if there are other forms of “life” or platforms other than carbon-based ones for life to continue? Not surprisingly, various alternative proposals have been set forth."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft24",
      "id": "fn24"
    }
  }, [_vm._v("24")])]), _vm._v(" One of them builds on the possibility of multiverses, which might make possible endless fertility. True, this universe may come to nil, but there are many—perhaps infinitely many—other universes in which fertility in one form or another may continue. Another highly speculative and creative suggestion is that of the physicist Freeman Dyson, who joins the argument that considers the existence of life a “necessity” in the kind of universe we know. This argument believes that (at least) intelligent life never comes to an end. With unprecedentedly enhanced capacities to manipulate and engineer our environment, we may bring into existence new kinds of genetic changes that guarantee the continuation of intelligence in some form, certainly not in the form of current human life (because, as said, that seems an absolute impossibility in light of current knowledge), but in some nonpersonal form."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft25",
      "id": "fn25"
    }
  }, [_vm._v("25")])]), _vm._v(" Yet another highly speculative proposal comes from an American cosmologist, Frank J. Tipler, a coauthor (with D. Barrow) of the highly acclaimed "), _c('em', [_vm._v("Anthropic Cosmological Principle")]), _vm._v(" in 1972. Materially similar to Dyson, Tipler’s vision of “physics of immortality” intuits the continuance of intelligent life as some kind of computational process—even long after any kind of intelligent life "), _c('a', {
    attrs: {
      "id": "page_30"
    }
  }, [_vm._v("as we know it has vanished. Indeed, he envisions that ultimately the infinite expansion of intelligence to everywhere in the universe is possible."), _c('sup')]), _c('a', {
    attrs: {
      "href": "#/#ft26",
      "id": "fn26"
    }
  }, [_vm._v("26")]), _vm._v(" These and other proposals, as said, are highly speculative and so far have no scientific warrant, let alone “evidence.”")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("What about the distant future prospects beyond the conditions of life on this earth and in this solar system? Well known and well documented is the possibility—as small as its probability is in a shorter time frame—of the earth being hit by a comet or asteroid."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft27",
      "id": "fn27"
    }
  }, [_vm._v("27")])]), _vm._v(" A familiar example from history is the dinosaur extinction due to a hit by a comet about 65 million years ago. As recently as 1994 we observed the Shoemaker-Levy comet’s damage on Jupiter. Asteroids, although smaller than comets, can also cause remarkable damage to our living conditions and can be equally deadly."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft28",
      "id": "fn28"
    }
  }, [_vm._v("28")])]), _vm._v(" The conclusion is bleak: “Whether or not a comet hits, we know for certain that in 5 billion years, the Earth will be uninhabitable. The Sun will come to the end of its available hydrogen fuel and will begin to swell up, its outer layers swallowing up Mercury, Venus and the Earth. It will then lose its outer layers and the centre will become a white dwarf, an object of high density about the size of the Earth. Without the heat and light of the Sun none of the remaining planets will be habitable.”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft29",
      "id": "fn29"
    }
  }, [_vm._v("29")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("For the sake of argument, let us suppose that by that time humankind would have found another habitable planet in our galaxy. In that case, a catastrophic scenario would seem to face us: “Galaxies have a local movement imposed on the general expansion of the Universe and one of our nearest neighbours, the Andromeda spiral galaxy, will crash into us.” This would make an escape from the sun’s death even more complicated."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft30",
      "id": "fn30"
    }
  }, [_vm._v("30")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("All the above-mentioned conditions are totally out of human control, as opposed to the relatively significant human factor concerning the near future of this earth and its life conditions. How likely are these conditions to "), _c('a', {
    attrs: {
      "id": "page_31"
    }
  }, [_vm._v("remain life-sustaining? The impending catastrophes facing the future, even the near future at least in terms of conditions for human life to survive on this globe, are well known and well documented. At the top of the imminent threats for human civilization is the devastating pollution"), _c('sup')]), _c('a', {
    attrs: {
      "href": "#/#ft31",
      "id": "fn31"
    }
  }, [_vm._v("31")]), _vm._v(" of the earth and her resources, causing dramatic climate changes."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft32",
      "id": "fn32"
    }
  }, [_vm._v("32")])]), _vm._v(" This pollution is considered to be “our world’s most dangerous, and constant, threat.”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft33",
      "id": "fn33"
    }
  }, [_vm._v("33")])]), _vm._v(" Impending environmental catastrophe is linked with a matrix of more or less interrelated problems such as scarcity of food to meet the needs of the growing population, deforestation, and overuse of fossil fuels; other forms of global pollution including waste problems, global warming, and the destruction of the ozone layer; and an increase in temperature with effects on the sea level and other ecosystemic areas."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft34",
      "id": "fn34"
    }
  }, [_vm._v("34")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("These and related developments on our globe mean that “if current trends continue, we will not.”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft35",
      "id": "fn35"
    }
  }, [_vm._v("35")])]), _vm._v(" Not surprisingly, among the leading scientists, pessimism about the future of our globe is common: “by 2600 the world’s population will be standing shoulder to shoulder, and the electricity use will make the Earth glow red hot.”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft36",
      "id": "fn36"
    }
  }, [_vm._v("36")])]), _vm._v(" As a result, “[the odds] are no better than fifty-fifty that our present civilisation on Earth will survive to the end of the present century.”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft37",
      "id": "fn37"
    }
  }, [_vm._v("37")])]), _vm._v(" Whereas the Enlightenment brought about an unprecedented optimism for the capacity of technology and science to produce ever-progressing life-sustaining and life-improving conditions, by the end of the second millennium those hopes were severely shattered and not infrequently abandoned."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft38",
      "id": "fn38"
    }
  }, [_vm._v("38")])]), _vm._v(" Here religions are not mere bystanders: religiously driven attitudes help shape environmental efforts or lack thereof. Only recently have scholars come to acknowledge in a more robust manner that not only “[h]uman beliefs about the nature of ecology are the distinctive contribution of our species to the ecology "), _c('a', {
    attrs: {
      "id": "page_32"
    }
  }, [_vm._v("itself” but that, importantly, “[r]eligious beliefs—especially those concerning the nature of powers that create and animate—become an effective part of ecological systems.”"), _c('sup')]), _c('a', {
    attrs: {
      "href": "#/#ft39",
      "id": "fn39"
    }
  }, [_vm._v("39")])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("Given these scientific predictions, it is no wonder various tactics either to defeat or at least escape the inevitable “end” have risen among scientists, a topic to which we turn next. Thereafter, we will consider what is at stake for theology and how theologians have reacted.")]), _vm._v(" "), _c('h1', {
    staticClass: "sec2",
    attrs: {
      "id": "sec2-10"
    }
  }, [_c('em', [_vm._v("How Scientists Have Responded to the Threat of the “End”")])]), _vm._v(" "), _c('p', {
    staticClass: "para1"
  }, [_vm._v("Polkinghorne summarizes neatly the most common responses of scientists to the necessary futility of the cosmos and its processes. First, with “stoic defiance in the face of threatened reality,” many consider the whole of human life (if not the rest of the cosmos) “no more than a transient episode.”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft40",
      "id": "fn40"
    }
  }, [_vm._v("40")])]), _vm._v(" Consider the pessimistic-realist take by the famous British philosopher Bertrand Russell: “All the labors of the ages … are destined to extinction in the vast death of the solar system, and the whole temple of man’s achievement must inevitably be buried beneath the debris of the universe in ruins.”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft41",
      "id": "fn41"
    }
  }, [_vm._v("41")])]), _vm._v(" While perhaps a noble attitude, for many other scientists the “grace of tragedy,” as S. Weinberg called it in his groundbreaking popular pamphlet "), _c('em', [_vm._v("The First Three Minutes")]), _vm._v(", hardly suffices."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft42",
      "id": "fn42"
    }
  }, [_vm._v("42")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("Other scientists find an alternative more appealing, one that can be called “a total view”: “While the universe may end badly, perhaps it is to the complete sweep of cosmic history, considered as a whole from beginning to end, that we should look if we wish to discern its significance. Meaning is to be found in the whole process and not in individual events.” The total view is aligned with a certain understanding of time, probably the most common among scientists (regardless of their view of the issue under discussion), namely, “block time,” that is, the denial of the commonsense flow-of-time conception. In this scenario, “meaning” is found in an “atemporally existent lifespan” of either the whole cosmos or an individual life in which distinctions "), _c('a', {
    attrs: {
      "id": "page_33"
    }
  }, [_vm._v("between past, present, and future are not real, any more than is the idea of progress (at least as far as it is related to time)."), _c('sup')]), _c('a', {
    attrs: {
      "href": "#/#ft43",
      "id": "fn43"
    }
  }, [_vm._v("43")]), _vm._v(" For a number of reasons, this way of responding to futility hardly satisfies all.")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("Yet another response is the so-called physical eschatology discussed above. In the form Dyson presented it, as the hope for the continuation of intellectual life beyond the destruction of this planet and perhaps even our galaxy, as long-term a scale as that may embrace, it is still penultimate. Only the proposal of Tipler—according to which the collapsing universe may be able to be reprogrammed, so to speak, with the help of an infinite amount of information to reach some kind of “omega” point—may carry some "), _c('em', [_vm._v("almost")]), _vm._v(" ultimate meaning; but that theory is the most speculative to scientists and does not have many followers."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft44",
      "id": "fn44"
    }
  }, [_vm._v("44")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("Finally, the response proposing endless fertility comes from those scientists who insist that quantum theory can be combined with general relativity theory—an enterprise totally impossible in light of current scientific knowledge and experience—to intuit “baby universes [that] are continually bubbling up from fluctuations in the primeval ur-state, which is the quantum gravitational vacuum.”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft45",
      "id": "fn45"
    }
  }, [_vm._v("45")])]), _vm._v(" It is believed that at least some of those “bubbles” may reach a cosmic lifetime, and hence, “[t]he cosmic pot will boil away ‘for ever.’ ” The problem with this proposal, provided it were at all possible (which it is not in light of contemporary science), is accurately noted by John Polkinghorne: “it would only present a scene of occasional islands of transient meaningfulness erupting within an ocean of absurdity.”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft46",
      "id": "fn46"
    }
  }, [_vm._v("46")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("Two short observations are in order before engaging theology. First, as all these speculations, including the naturalistic Stoicism (option 1 above), are just that, "), _c('em', [_vm._v("speculations")]), _vm._v(", none of them merits any kind of scientific consensus. Second, related to the first, is the simple but often ignored point that empirical science, to be science, in principle has no answer to these kinds of philosophical and metaphysical queries. Even when scientists speak to these issues, they do not do so on the basis of science (alone) but have moved out of its own self-formulated boundaries. (Too bad most scientists hardly recognize this issue, or at least are not making it clear to their wider audience.) That is one important reason why philosophers, scholars of religion, and theologians are needed and permitted to engage the conversation about the “end.”")]), _vm._v(" "), _c('h1', {
    staticClass: "sec1",
    attrs: {
      "id": "sec-7"
    }
  }, [_c('a', {
    attrs: {
      "id": "page_34"
    }
  }, [_c('strong', [_vm._v("Sciences and Theology in Critical Mutual Dialogue about “Eschatology”")])])]), _c('a', {
    attrs: {
      "id": "page_34"
    }
  }, [_c('h1', {
    staticClass: "sec2-t0",
    attrs: {
      "id": "sec2-11"
    }
  }, [_c('em', [_vm._v("For Orientation: What Is at Stake for Theology?")])])]), _c('p', {
    staticClass: "para1"
  }, [_c('a', {
    attrs: {
      "id": "page_34"
    }
  }, [_vm._v("Briefly and nontechnically put, the all-important and central challenge to theology from the point of view of physical cosmology’s predictions about the fate of the universe is this: How can we even begin to reconcile Christian eschatology’s expectation of the imminent return of Christ and the bringing about of the “new heavens and new earth” in light of the extremely, almost infinitely long horizons of sciences?"), _c('sup')]), _c('a', {
    attrs: {
      "href": "#/#ft47",
      "id": "fn47"
    }
  }, [_vm._v("47")]), _vm._v(" This was the way Pannenberg posed his last question to scientists: “Is the Christian affirmation of an imminent end of this world that in some way invades the present somehow reconcilable with scientific extrapolations of the continuing existence of the universe for at least several billions of years ahead?” The obvious dissonance comes to the fore in that “[s]cientific predictions that in some comfortably distant future the conditions for life will no longer continue on our planet are hardly comparable to biblical eschatology.”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft48",
      "id": "fn48"
    }
  }, [_vm._v("48")])]), _vm._v(" Wisely, Pannenberg acknowledges that “[t]o this question there are no easy solutions,” and therefore, at least for the time being, theologians perhaps should just accept the tension and conflict."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft49",
      "id": "fn49"
    }
  }, [_vm._v("49")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("The mutual tension between the two disciplines is intensified by the fact that Christian theology boldly claims that although the present universe will end, it will give way to a transformed reality “to which the natural sciences provide no access, and to which our human experience gives us only obscure, but nevertheless real, intimations and indications.”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft50",
      "id": "fn50"
    }
  }, [_vm._v("50")])]), _vm._v(" An even greater challenge to theology is what Robert J. Russell claims: “Eschatology as necessarily including not only the resurrection of the body but also the transformation into the new creation by a new act of the Trinitarian God … [as] a foundational commitment”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft51",
      "id": "fn51"
    }
  }, [_vm._v("51")])]), _vm._v(" is in no way limiting its vision to the “religious” or personal spheres but embraces the whole cosmic horizon.")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("To a great extent, the type (or even lack) of theological response and initiative depends on the wider approach to the difficult question of how these "), _c('a', {
    attrs: {
      "id": "page_35"
    }
  }, [_vm._v("two disciplines can be related to each other and, should mutual dialogue be desired, under what conditions that might happen. To the clarification of these issues we turn last in this chapter.")])]), _c('a', {
    attrs: {
      "id": "page_35"
    }
  }, [_c('h1', {
    staticClass: "sec2",
    attrs: {
      "id": "sec2-12"
    }
  }, [_c('em', [_vm._v("Bridging the Gap between the Natural Sciences and Theology: Obstacles and Opportunities")])])]), _c('p', {
    staticClass: "para1"
  }, [_c('a', {
    attrs: {
      "id": "page_35"
    }
  }, [_vm._v("The main reason for theology (in this case, the theology of “last things”) to engage science is not simply because of the “sheer magnitude of its reach”"), _c('sup')]), _c('a', {
    attrs: {
      "href": "#/#ft52",
      "id": "fn52"
    }
  }, [_vm._v("52")]), _vm._v(" (which alone should make any religion interested in the dialogue),"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft53",
      "id": "fn53"
    }
  }, [_vm._v("53")])]), _vm._v(" but rather because there is a solid "), _c('em', [_vm._v("theological")]), _vm._v(" demand for it: when “Christians confess God as the Creator of the world, it is inevitably the same world that is the object of scientific descriptions.” As a result, theologians have the task of relating their statements to those of scientists."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft54",
      "id": "fn54"
    }
  }, [_vm._v("54")])]), _vm._v(" While some see every scientific advancement as a step away from religion and faith, it can also be argued that “science has actually advanced to the point where what were formerly religious questions can be seriously tackled.”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft55",
      "id": "fn55"
    }
  }, [_vm._v("55")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("Understandably, various ways of relating to science have emerged among the theologians and theological movements. We can condense them into two broad categories."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft56",
      "id": "fn56"
    }
  }, [_vm._v("56")])]), _vm._v(" The first can be called “separation,” in which science and theology are considered two separate realms. As a result, no conflict arises between sciences’ predictions about the gradual, very slow decay of everything and Christian faith’s expectation of the kingdom’s imminent arrival. Classical liberalism’s split between “nature,” the domain of scientific inquiry, and “history,” the area of humanist disciplines, laid the foundation for it."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft57",
      "id": "fn57"
    }
  }, [_vm._v("57")])]), _vm._v(" In this reductionist outlook, the question could be raised, “Does matter really matter? It would be easy to argue that Christian faith seems to look forward only to the existence of a disembodied soul after death in a non physical spiritual "), _c('a', {
    attrs: {
      "id": "page_36"
    }
  }, [_vm._v("heaven.”"), _c('sup')]), _c('a', {
    attrs: {
      "href": "#/#ft58",
      "id": "fn58"
    }
  }, [_vm._v("58")]), _vm._v(" A related foundational weakness of classical liberalism has to do with the fully “futuristic-mythical” eschatology of William Wrede or Albert Schweitzer. It simply is toothless in relation to the concerns of physical cosmology, which is anchored in the material realities of the present world. Nor does the “realized eschatology”—either in the way it was first advocated by the British New Testament scholar C. H. Dodd or more recently by the American theologian Kathryn Tanner—have much to say to sciences’ views of the future, even though the latter has also engaged science-religion conversation. Yet another highly important and much more nuanced example of the separation paradigm comes from a surprising scholar, namely, one of the pioneers and leading experts in the field, the late biologist-priest Arthur Peacocke. Counterintuitively to many of his colleagues, Peacocke believed that theology has little to say to sciences about the future and that sciences and theology occupy their separate venues. Indeed, he went so far as to surmise that “[a]ll speculation on detailed scenarios of this consummation, the theological exercise called ‘eschatology,’ surely constitutes a supreme example of attempting to formulate a theory underdetermined by the facts. As such, it seems to me a fruitless and unnecessary exercise.”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft59",
      "id": "fn59"
    }
  }, [_vm._v("59")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("All these forms of a “separation” approach are in some way or another supported strongly by the prevailing (metaphysical) naturalism rampant among natural scientists. Whereas for theologians, the separation mentality is funded by the desire to avoid conflict with the dominant scientific paradigm, for naturalist scientists, the separation results from the total incredibility of theological (and other “humanistic”) explanations: “According to a widespread prejudice, eschatological symbols seem to point to a realm ‘totally other’ than the reality we can experience.”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft60",
      "id": "fn60"
    }
  }, [_vm._v("60")])]), _vm._v(" Naturalist worldviews also eschew all teleological notions (other than those that in some sense can be attributed to randomness and chance, that is, evolutionary features considered to be an “improvement”). For theology, however, purposefulness is a necessary category for the simple reason that “if past history were to lack meaning, there would be no reason to anticipate future fulfillment”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft61",
      "id": "fn61"
    }
  }, [_vm._v("61")])]), _vm._v(" (even when the “newness” of God’s future hardly can be extrapolated in any simple manner from the past processes).")]), _vm._v(" "), _c('h1', {
    staticClass: "sec2",
    attrs: {
      "id": "sec2-13"
    }
  }, [_c('a', {
    attrs: {
      "id": "page_37"
    }
  }, [_c('em', [_vm._v("On the Conditions and Promise of a Mutual Critical Dialogue")])])]), _c('a', {
    attrs: {
      "id": "page_37"
    }
  }), _c('p', {
    staticClass: "para1"
  }, [_c('a', {
    attrs: {
      "id": "page_37"
    }
  }, [_vm._v("As a corrective to these and related limited visions, slowly and incrementally a wider and more comprehensive outlook for theology began to emerge in the mid-twentieth century."), _c('sup')]), _c('a', {
    attrs: {
      "href": "#/#ft62",
      "id": "fn62"
    }
  }, [_vm._v("62")]), _vm._v(" Significantly, Vatican II already hinted at it when stating that the church “will attain its full perfection only in the glory of heaven, when there will come the time of the restoration of all things [when] the entire world, which is intimately related to man and attains to its end through him, will be perfectly reestablished in Christ.”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft63",
      "id": "fn63"
    }
  }, [_vm._v("63")])]), _vm._v(" The same can be said of the Lutheran Paul Tillich’s intuition that without the consideration of the end of the cosmos, the ultimate end of humanity cannot be properly understood."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft64",
      "id": "fn64"
    }
  }, [_vm._v("64")])]), _vm._v(" That said, for him the “universe” seems to have meant less the vast, almost infinite universe of current physical cosmology and more something like the “history” of this planet."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft65",
      "id": "fn65"
    }
  }, [_vm._v("65")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("Gleaning from these and similar impulses and making a “cosmic” orientation to theology in general and eschatology in particular a defining theme, a growing number of contemporary theologians have become convinced that because humanity would not be here without the long cosmic processes, there cannot be a final and ultimate salvation of humanity and fulfillment of divine promises without the transformation of not only our earth and its creatures but also the whole cosmos."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft66",
      "id": "fn66"
    }
  }, [_vm._v("66")])]), _vm._v(" Hence, the engagement of sciences is necessary. A number of science-theology experts have paved the way for this approach, including W. Pannenberg, J. Moltmann, R. Russell, D. Wilkinson, J. Polkinghorne, T. Peters, and D. Edwards, among others. Some scientists, such as the leading evolutionary paleobiologist Simon Conway Morris, have also spoken for the common interests between science and eschatology, notwithstanding radically different agendas in the main."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft67",
      "id": "fn67"
    }
  }, [_vm._v("67")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("Both sides must clearly understand the many and varied challenges to this mutual dialogue. The basic dilemma simply is that “[t]he idea of a hope after death and an end that fulfills history as a whole is as intrinsic to the "), _c('a', {
    attrs: {
      "id": "page_38"
    }
  }, [_vm._v("Christian tradition as it is foreign to the project of science.”"), _c('sup')]), _c('a', {
    attrs: {
      "href": "#/#ft68",
      "id": "fn68"
    }
  }, [_vm._v("68")]), _vm._v(" Indeed, not only for most scientists but also for the wider public, any religious claims for the consummation of eschatological hope seem not only erroneous but even fanciful and without any factual content."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft69",
      "id": "fn69"
    }
  }, [_vm._v("69")])]), _vm._v(" That is fully understandable in light of current science, which tells us that “our universe is condemned to a final futility and will end either in a big crunch, or go on forever expanding in continual disintegration and decay, long after all life has vanished. To believe that Christ is risen and will come again is to insist that laws of nature, with their steady march toward disintegration, decay, and death, are not the ultimate reality.”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft70",
      "id": "fn70"
    }
  }, [_vm._v("70")])]), _vm._v(" In that light, it is significant what Jewish theologian Neil Gillman discerns about a major difference between the biblical/theological and the scientific cosmology, namely, that the latter “is totally predetermined. It is set in motion by the Big Bang and will run its course come what may. Whereas in the biblical account, the endtime will be determined by the will of God, either when the world will have sunk into a state of abominable evil, or, as in a different tradition, when the world will have achieved a level of sanctity.”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft71",
      "id": "fn71"
    }
  }, [_vm._v("71")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("For these two inquiries to collaborate fruitfully, it has to be agreed that the dialogue between theology and science cannot be a one-way street: it must be a give-and-take conversation."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft72",
      "id": "fn72"
    }
  }, [_vm._v("72")])]), _vm._v(" True, at the moment, most theologians should devote much energy to learning even the basics of scientific cosmology and integrate those basic principles into their own theological reflection. At the same time, we should follow Pannenberg, who posed “theological questions to scientists,” the last five of which engage eschatology."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft73",
      "id": "fn73"
    }
  }, [_vm._v("73")])]), _vm._v(" Theology’s challenge to scientists is that “an unaided scientific account of the world does not succeed in making complete sense of cosmic history”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft74",
      "id": "fn74"
    }
  }, [_vm._v("74")])]), _vm._v(" because “science, which can only tell the horizontal story of unfolding present process, is not in a position either to deny or confirm this ‘vertical’ story of divine faithfulness.”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft75",
      "id": "fn75"
    }
  }, [_vm._v("75")])]), _vm._v(" Christian theology boldly claims that “although the human species, life, the earth, the sun, and the universe will end, that end is not ultimate but somehow leads to a fuller, transformed reality, to which the natural sciences provide no access, "), _c('a', {
    attrs: {
      "id": "page_39"
    }
  }, [_vm._v("and to which our human experience gives us only obscure, but nevertheless real, intimations and indications.”"), _c('sup')]), _c('a', {
    attrs: {
      "href": "#/#ft76",
      "id": "fn76"
    }
  }, [_vm._v("76")])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("Indeed, rightly understood—and without in any way downplaying the clear demarcation lines between (natural) sciences and theology—we need to be reminded that “[b]oth science and theology have to speak about unseen realities” as they go about their investigation. Physicists deal with phenomena such as quarks and gluons (and many other, if possible, even “smaller” items of reality), which are of course totally unseen, not only because of yet-to-be-improved tools of investigation but also because of their very nature, that is, their almost infinitely small size! Their existence and workings are believed to be real because they help explain many processes and phenomena that are observable. Consider this familiar example to scientists: “When high-energy projectiles impinge on protons and neutrons in what is called a deep inelastic scattering process, they ‘bounce back’ in just the way that corresponds to their hitting pointlike quark constituents inside.” Somewhat similarly, theologians’ beliefs in doctrines such as divine action or providence may be funded at least partially by accumulated personal and communal experiences on the basis of which inferences are drawn and continuously tested, including their compatibility with texts and other testimonies of sacred tradition."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/#ft77",
      "id": "fn77"
    }
  }, [_vm._v("77")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("Having inquired into the resources of natural sciences with regard to the “future” and “end” of the cosmos and life, we engage another preparatory task before "), _c('span', {
    attrs: {
      "href": "#/012_Chapter04#ch-4",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("chapter 4")]), _vm._v(" delves into distinctively Christian theological resources—the investigation into four living faith traditions’ eschatological intuitions.")]), _vm._v(" "), _c('p', {
    staticClass: "image"
  }, [_c('img', {
    attrs: {
      "alt": "image",
      "src": "images/foot.jpg"
    }
  })]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn1",
      "id": "ft1"
    }
  }, [_vm._v("1")]), _vm._v(". This summary repeats the materials from my "), _c('em', [_vm._v("Spirit and Salvation")]), _vm._v(", chap. 3; both that chapter and this are based on the detailed discussion in part 1 of "), _c('em', [_vm._v("Creation and Humanity")]), _vm._v(". For a highly useful presentation of “the first three minutes” to nonspecialists, see chap. 3 in P. Davies, "), _c('em', [_vm._v("The Last Three Minutes")]), _vm._v(". From a theological perspective, see Barr, “Modern Cosmology and Christian Theology.”")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn2",
      "id": "ft2"
    }
  }, [_vm._v("2")]), _vm._v(". See further McGrath, "), _c('em', [_vm._v("A Scientific Theology")]), _vm._v(", 2:283–85; R. Russell, “Quantum Physics,” pp. 346–48.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn3",
      "id": "ft3"
    }
  }, [_vm._v("3")]), _vm._v(". Clayton, "), _c('em', [_vm._v("Mind and Emergence")]), _vm._v(", p. vi; for the history, see chap. 1.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn4",
      "id": "ft4"
    }
  }, [_vm._v("4")]), _vm._v(". Haught, "), _c('em', [_vm._v("Is Nature Enough?")]), _vm._v(" p. 86.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn5",
      "id": "ft5"
    }
  }, [_vm._v("5")]), _vm._v(". Pannenberg, "), _c('em', [_vm._v("ST")]), _vm._v(" 2:112.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn6",
      "id": "ft6"
    }
  }, [_vm._v("6")]), _vm._v(". See Wilkinson, "), _c('em', [_vm._v("Christian Eschatology")]), _vm._v(", pp. 5–6.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn7",
      "id": "ft7"
    }
  }, [_vm._v("7")]), _vm._v(". See P. Davies, "), _c('em', [_vm._v("The Last Three Minutes")]), _vm._v(", pp. x–xi.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn8",
      "id": "ft8"
    }
  }, [_vm._v("8")]), _vm._v(". For a succinct account, see Barrow, “The Far, Far Future,” pp. 26–28 particularly; Worthing, "), _c('em', [_vm._v("God, Creation, and Contemporary Physics")]), _vm._v(", pp. 160–62. See also Rees, “Living in a Multiverse,” pp. 77–82.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn9",
      "id": "ft9"
    }
  }, [_vm._v("9")]), _vm._v(". See Wilkinson, "), _c('em', [_vm._v("Christian Eschatology")]), _vm._v(", p. ix. For predictions of the future fate of the cosmos among scientists, see, e.g., P. Ward and Brownlee, "), _c('em', [_vm._v("The Life and Death of Planet Earth")]), _vm._v(".")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn10",
      "id": "ft10"
    }
  }, [_vm._v("10")]), _vm._v(". See further P. Davies, "), _c('em', [_vm._v("The Last Three Minutes")]), _vm._v(", chap. 2: “The Dying Universe.”")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn11",
      "id": "ft11"
    }
  }, [_vm._v("11")]), _vm._v(". Wilkinson, "), _c('em', [_vm._v("Christian Eschatology")]), _vm._v(", pp. 12, 15.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn12",
      "id": "ft12"
    }
  }, [_vm._v("12")]), _vm._v(". Barrow, “The Far, Far Future,” pp. 30–33; for various “scientific cosmological options” concerning the “beginning” and “end” of the universe, see the succinct statement in P. Davies, “Eternity,” pp. 42–44.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn13",
      "id": "ft13"
    }
  }, [_vm._v("13")]), _vm._v(". See P. Davies, “Eternity,” p. 50.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn14",
      "id": "ft14"
    }
  }, [_vm._v("14")]), _vm._v(". R. Russell, "), _c('em', [_vm._v("Time in Eternity")]), _vm._v(", pp. 56–59 (and the extensive references therein, both nontechnical and technical); see also Worthing, "), _c('em', [_vm._v("God, Creation, and Contemporary Physics")]), _vm._v(", pp. 162–75.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn15",
      "id": "ft15"
    }
  }, [_vm._v("15")]), _vm._v(". See R. Russell, "), _c('em', [_vm._v("Time in Eternity")]), _vm._v(", pp. 59–60 (59). For a nontechnical discussion, see chap. 5 in Islam, "), _c('em', [_vm._v("The Ultimate Fate of the Universe")]), _vm._v(".")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn16",
      "id": "ft16"
    }
  }, [_vm._v("16")]), _vm._v(". Barrow, “The Far, Far Future,” pp. 36–37.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn17",
      "id": "ft17"
    }
  }, [_vm._v("17")]), _vm._v(". With minor adaptations from Boisvert, "), _c('em', [_vm._v("Religion and the Physical Sciences")]), _vm._v(", pp. 229–34.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn18",
      "id": "ft18"
    }
  }, [_vm._v("18")]), _vm._v(". The predictions about the future of the cosmos did not of course begin in recent decades. A noteworthy historical precedent is the 1755 work by Immanuel Kant, "), _c('em', [_vm._v("Universal Natural History")]), _vm._v(", pp. 59–70; clearly the size of the universe was dramatically smaller in the imagination of Kant and other scholars of the time. For other historical predictions, see Barrow, “The Far, Far Future,” pp. 23–26.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn19",
      "id": "ft19"
    }
  }, [_vm._v("19")]), _vm._v(". The figure 10"), _c('sup', [_vm._v("12")]), _vm._v(" years = 10,000,000,000,000 years; and likewise for other similar figures.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn20",
      "id": "ft20"
    }
  }, [_vm._v("20")]), _vm._v(". R. Russell, “Cosmology and Eschatology,” in "), _c('em', [_vm._v("OHE")]), _vm._v(", p. 566; a similar, slightly more detailed list can be found in R. Russell, "), _c('em', [_vm._v("Time in Eternity")]), _vm._v(", pp. 60–61. The main source referred to is Barrow and Tipler, "), _c('em', [_vm._v("Anthropic Cosmological Principle")]), _vm._v(", pp. 653–54.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn21",
      "id": "ft21"
    }
  }, [_vm._v("21")]), _vm._v(". Barrow and Tipler, "), _c('em', [_vm._v("Anthropic Cosmological Principle")]), _vm._v(", p. 684, cited in R. Russell, "), _c('em', [_vm._v("Time in Eternity")]), _vm._v(", p. 61.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn22",
      "id": "ft22"
    }
  }, [_vm._v("22")]), _vm._v(". R. Russell, "), _c('em', [_vm._v("Time in Eternity")]), _vm._v(", p. 61.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn23",
      "id": "ft23"
    }
  }, [_vm._v("23")]), _vm._v(". Barrow, “Cosmology,” 4.8, cited in Wilkinson, "), _c('em', [_vm._v("Christian Eschatology")]), _vm._v(", p. 15.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn24",
      "id": "ft24"
    }
  }, [_vm._v("24")]), _vm._v(". This paragraph is indebted to Wilkinson, "), _c('em', [_vm._v("Christian Eschatology")]), _vm._v(", pp. 7–10.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn25",
      "id": "ft25"
    }
  }, [_vm._v("25")]), _vm._v(". For his early suggestion, see Dyson, “Time without End,” pp. 449–50; for a recent (more) accessible account, see his “Life in the Universe,” pp. 140–57.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn26",
      "id": "ft26"
    }
  }, [_vm._v("26")]), _vm._v(". Tipler, "), _c('em', [_vm._v("Physics of Immortality")]), _vm._v("; for an insightful theological engagement and critique, see R. Russell, “Cosmology and Eschatology: The Implications,” pp. 195–216. Also highly useful is Worthing, "), _c('em', [_vm._v("God, Creation, and Contemporary Physics")]), _vm._v(", pp. 164–75.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn27",
      "id": "ft27"
    }
  }, [_vm._v("27")]), _vm._v(". For a vivid imaginary account of comet Swift-Tuttle and other similar ones hitting the earth in the near future, see chap. 2 in P. Davies, "), _c('em', [_vm._v("The Last Three Minutes")]), _vm._v(".")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn28",
      "id": "ft28"
    }
  }, [_vm._v("28")]), _vm._v(". Taken from Wilkinson, "), _c('em', [_vm._v("Christian Eschatology")]), _vm._v(", chap. 2; Stoeger, “Scientific Accounts of Ultimate Catastrophes,” pp. 20–24; Polkinghorne, "), _c('em', [_vm._v("God of Hope")]), _vm._v(", pp. 6–8. For a now-classic presentation, see Asimov, "), _c('em', [_vm._v("A Choice of Catastrophes")]), _vm._v(".")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn29",
      "id": "ft29"
    }
  }, [_vm._v("29")]), _vm._v(". Wilkinson, "), _c('em', [_vm._v("Christian Eschatology")]), _vm._v(", p. 9.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn30",
      "id": "ft30"
    }
  }, [_vm._v("30")]), _vm._v(". Wilkinson, "), _c('em', [_vm._v("Christian Eschatology")]), _vm._v(", p. 10; for details, see also Stoeger, “Scientific Accounts of Ultimate Catastrophes,” pp. 24–27; Islam, "), _c('em', [_vm._v("The Ultimate Fate of the Universe")]), _vm._v(", chap. 11.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn31",
      "id": "ft31"
    }
  }, [_vm._v("31")]), _vm._v(". In this discussion “pollution” is used as an inclusive umbrella term denoting all types of harm to the environment and its processes and creatures by humans (whether directly or indirectly).")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn32",
      "id": "ft32"
    }
  }, [_vm._v("32")]), _vm._v(". The famous “Gaia hypothesis” was proposed by James Lovelock; for a formative essay, see his "), _c('em', [_vm._v("The Revenge of Gaia")]), _vm._v(".")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn33",
      "id": "ft33"
    }
  }, [_vm._v("33")]), _vm._v(". International Conference on Environmental Pollution and Remediation, "), _c('em', [_vm._v("3rd International Conference")]), _vm._v(".")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn34",
      "id": "ft34"
    }
  }, [_vm._v("34")]), _vm._v(". For an up-to-date discussion, see Brunner, Butler, and Swoboda, "), _c('em', [_vm._v("Introducing Evangelical Ecotheology")]), _vm._v(", pp. 49–62.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn35",
      "id": "ft35"
    }
  }, [_vm._v("35")]), _vm._v(". Maguire, "), _c('em', [_vm._v("Moral Core of Judaism and Christianity")]), _vm._v(", p. 13.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn36",
      "id": "ft36"
    }
  }, [_vm._v("36")]), _vm._v(". Hawking, "), _c('em', [_vm._v("The Universe in a Nutshell")]), _vm._v(", p. 158, cited in Wilkinson, "), _c('em', [_vm._v("Christian Eschatology")]), _vm._v(", p. 1.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn37",
      "id": "ft37"
    }
  }, [_vm._v("37")]), _vm._v(". Rees, "), _c('em', [_vm._v("Our Final Hour")]), _vm._v(", p. 8, cited in Wilkinson, "), _c('em', [_vm._v("Christian Eschatology")]), _vm._v(", p. 1.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn38",
      "id": "ft38"
    }
  }, [_vm._v("38")]), _vm._v(". See Wilkinson, "), _c('em', [_vm._v("Christian Eschatology")]), _vm._v(", p. 7.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn39",
      "id": "ft39"
    }
  }, [_vm._v("39")]), _vm._v(". L. Sullivan, preface to "), _c('em', [_vm._v("Hinduism and Ecology")]), _vm._v(", p. xi.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn40",
      "id": "ft40"
    }
  }, [_vm._v("40")]), _vm._v(". Polkinghorne, "), _c('em', [_vm._v("God of Hope")]), _vm._v(", p. 21.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn41",
      "id": "ft41"
    }
  }, [_vm._v("41")]), _vm._v(". B. Russell, “Free Man’s Worship,” p. 41, quoted in R. Russell, “Cosmology and Eschatology,” in "), _c('em', [_vm._v("OHE")]), _vm._v(", p. 567 (which also contains several other similar statements by leading scientists and philosophers).")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn42",
      "id": "ft42"
    }
  }, [_vm._v("42")]), _vm._v(". Weinberg, "), _c('em', [_vm._v("The First Three Minutes")]), _vm._v(", p. 155, cited (from an earlier edition) in Polkinghorne, "), _c('em', [_vm._v("God of Hope")]), _vm._v(", p. 21.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn43",
      "id": "ft43"
    }
  }, [_vm._v("43")]), _vm._v(". Polkinghorne, "), _c('em', [_vm._v("God of Hope")]), _vm._v(", pp. 22–23.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn44",
      "id": "ft44"
    }
  }, [_vm._v("44")]), _vm._v(". See Polkinghorne, "), _c('em', [_vm._v("God of Hope")]), _vm._v(", pp. 23–26.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn45",
      "id": "ft45"
    }
  }, [_vm._v("45")]), _vm._v(". On “the future of the vacuum” and its implications to our topic, see Barrow, “The Far, Far Future,” pp. 33–36.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn46",
      "id": "ft46"
    }
  }, [_vm._v("46")]), _vm._v(". Polkinghorne, "), _c('em', [_vm._v("God of Hope")]), _vm._v(", pp. 26–27.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn47",
      "id": "ft47"
    }
  }, [_vm._v("47")]), _vm._v(". For details, see Worthing, "), _c('em', [_vm._v("God, Creation, and Contemporary Physics")]), _vm._v(", pp. 175–79. Note his title for "), _c('span', {
    attrs: {
      "href": "#/013_Chapter05#ch-5",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("chap. 5")]), _vm._v(", which discusses eschatology: “Can God Survive the Consummation of the Universe?”")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn48",
      "id": "ft48"
    }
  }, [_vm._v("48")]), _vm._v(". Pannenberg, “Theological Questions to Scientists,” p. 48; see also his "), _c('em', [_vm._v("ST")]), _vm._v(" 3:589–90.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn49",
      "id": "ft49"
    }
  }, [_vm._v("49")]), _vm._v(". Pannenberg, “Theological Questions to Scientists,” p. 48.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn50",
      "id": "ft50"
    }
  }, [_vm._v("50")]), _vm._v(". Stoeger, “Scientific Accounts of Ultimate Catastrophes,” p. 20.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn51",
      "id": "ft51"
    }
  }, [_vm._v("51")]), _vm._v(". R. Russell, "), _c('em', [_vm._v("Time in Eternity")]), _vm._v(", p. 67; for discussion of the vision of some of the said scholars, see pp. 67–70.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn52",
      "id": "ft52"
    }
  }, [_vm._v("52")]), _vm._v(". See Iqbal, “Islam and Modern Science,” p. 3.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn53",
      "id": "ft53"
    }
  }, [_vm._v("53")]), _vm._v(". Rashed, “The End Matters,” p. 37. For a current massive discussion, see McNamara and Wildman, eds., "), _c('em', [_vm._v("Science and the World’s Religions")]), _vm._v(".")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn54",
      "id": "ft54"
    }
  }, [_vm._v("54")]), _vm._v(". Pannenberg, “Contributions from Systematic Theology,” p. 359.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn55",
      "id": "ft55"
    }
  }, [_vm._v("55")]), _vm._v(". P. Davies, "), _c('em', [_vm._v("God and the New Physics")]), _vm._v(", p. ix.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn56",
      "id": "ft56"
    }
  }, [_vm._v("56")]), _vm._v(". For a fourfold typology (both in Christian theology and four living faith traditions), see my "), _c('em', [_vm._v("Creation and Humanity")]), _vm._v(", chap. 2. For a recent useful discussion, see also Stenmark, “How to Relate Christian Faith and Science.”")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn57",
      "id": "ft57"
    }
  }, [_vm._v("57")]), _vm._v(". For a highly insightful and nuanced theological reflection, see Welker, “Springing Cultural Traps”; also useful is his earlier essay “Creation.”")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn58",
      "id": "ft58"
    }
  }, [_vm._v("58")]), _vm._v(". Wilkinson, "), _c('em', [_vm._v("Christian Eschatology")]), _vm._v(", p. ix.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn59",
      "id": "ft59"
    }
  }, [_vm._v("59")]), _vm._v(". Peacocke, "), _c('em', [_vm._v("Paths from Science towards God")]), _vm._v(", p. 48, cited in Wilkinson, "), _c('em', [_vm._v("Christian Eschatology")]), _vm._v(", p. 48. For similar comments by others, see R. Russell, "), _c('em', [_vm._v("Time in Eternity")]), _vm._v(", pp. 64–65.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn60",
      "id": "ft60"
    }
  }, [_vm._v("60")]), _vm._v(". Welker, “Theological Realism,” p. 34.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn61",
      "id": "ft61"
    }
  }, [_vm._v("61")]), _vm._v(". Polkinghorne, “Eschatological Credibility,” p. 43.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn62",
      "id": "ft62"
    }
  }, [_vm._v("62")]), _vm._v(". Naming it “The Method of Creative Mutual Interaction,” R. Russell has refined its details with much insight and skill. For one of the latest accounts, see his "), _c('em', [_vm._v("Time in Eternity")]), _vm._v(", pp. 70–83.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn63",
      "id": "ft63"
    }
  }, [_vm._v("63")]), _vm._v(". "), _c('em', [_vm._v("Lumen Gentium")]), _vm._v(", #48.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn64",
      "id": "ft64"
    }
  }, [_vm._v("64")]), _vm._v(". Tillich, "), _c('em', [_vm._v("ST")]), _vm._v(" 3:396.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn65",
      "id": "ft65"
    }
  }, [_vm._v("65")]), _vm._v(". See also Wilkinson, "), _c('em', [_vm._v("Christian Eschatology")]), _vm._v(", p. 3, for turning my attention to this particular passage.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn66",
      "id": "ft66"
    }
  }, [_vm._v("66")]), _vm._v(". See the important comments in Moltmann, "), _c('em', [_vm._v("Coming of God")]), _vm._v(", pp. 259–60.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn67",
      "id": "ft67"
    }
  }, [_vm._v("67")]), _vm._v(". Morris, “Does Biology Have an Eschatology?” pp. 158–74.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn68",
      "id": "ft68"
    }
  }, [_vm._v("68")]), _vm._v(". Clayton, “Eschatology as Metaphysics,” p. 134.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn69",
      "id": "ft69"
    }
  }, [_vm._v("69")]), _vm._v(". See, e.g., Stoeger, “Scientific Accounts of Ultimate Catastrophes,” p. 19.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn70",
      "id": "ft70"
    }
  }, [_vm._v("70")]), _vm._v(". J. Walls, introduction to "), _c('em', [_vm._v("OHE")]), _vm._v(", p. 6.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn71",
      "id": "ft71"
    }
  }, [_vm._v("71")]), _vm._v(". Gillman, “How Will It All End?” p. 46.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn72",
      "id": "ft72"
    }
  }, [_vm._v("72")]), _vm._v(". So also Peters, “Introduction,” p. xii.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn73",
      "id": "ft73"
    }
  }, [_vm._v("73")]), _vm._v(". Pannenberg, “Theological Questions to Scientists,” p. 48.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn74",
      "id": "ft74"
    }
  }, [_vm._v("74")]), _vm._v(". Polkinghorne, “Eschatology,” p. 38; see also Pannenberg, “Laying Theological Claim to Scientific Understandings,” pp. 51–64.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn75",
      "id": "ft75"
    }
  }, [_vm._v("75")]), _vm._v(". Polkinghorne, "), _c('em', [_vm._v("Science and Religion in Quest of Truth")]), _vm._v(", p. 102.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn76",
      "id": "ft76"
    }
  }, [_vm._v("76")]), _vm._v(". Stoeger, “Scientific Accounts of Ultimate Catastrophes,” p. 20.")]), _vm._v(" "), _c('p', {
    staticClass: "foot-notes"
  }, [_c('a', {
    attrs: {
      "href": "#/#fn77",
      "id": "ft77"
    }
  }, [_vm._v("77")]), _vm._v(". Polkinghorne and Welker, “Introduction,” pp. 3–4 (4). For thoughtful reflections on the “human” nature of science, see Coyne, “Seeking the Future,” pp. 13–14.")])])])
}]}


//////////////////
// WEBPACK FOOTER
// ../node_modules/vue-loader/lib/template-compiler?{"id":"data-v-27da2890","hasScoped":false,"transformToRequire":{"video":"src","source":"src","img":"src","image":"xlink:href"}}!../node_modules/vue-loader/lib/selector.js?type=template&index=0!./src/components/010_Chapter02.vue
// module id = 84
// module chunks = 0